import { alpha, Theme } from '@mui/material/styles';
import type { AppThemeId } from '../themes/themeRegistry';
import {
  COPA_AMARELO_BANDEIRA,
  COPA_VERDE_BANDEIRA,
  copaDrawerPaperSx,
  copaMenuItemSx,
  copaSidebarBrandSx,
  copaSidebarUserBoxSx,
} from '../styles/customThemes/copa';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_WIDTH_COLLAPSED = 60;
/** Altura do AppBar no mobile (xs) — drawer começa abaixo dele */
export const MOBILE_APP_BAR_HEIGHT = 53;
/** Altura do AppBar no desktop (sm+) */
export const DESKTOP_APP_BAR_HEIGHT = 50;

/** Remove prefixo "- " dos submenus */
export function formatMenuLabel(text: string): string {
  return text.replace(/^-\s*/, '');
}

export function capitalizeRole(role: string): string {
  if (!role) return '';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

export function getSidebarPaperSx(
  theme: Theme,
  expanded: boolean,
  isMobile: boolean,
  themeId: AppThemeId = 'default',
) {
  const isDark = themeId === 'dark';
  const isCopa = themeId === 'copa';

  const layoutSx = {
    boxSizing: 'border-box' as const,
    width: isMobile ? SIDEBAR_WIDTH * 1.3 : expanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    position: 'fixed' as const,
    ...(isMobile
      ? {
          top: MOBILE_APP_BAR_HEIGHT,
          height: `calc(100% - ${MOBILE_APP_BAR_HEIGHT}px)`,
          zIndex: theme.zIndex.drawer,
        }
      : {
          top: 0,
          height: '100%',
          zIndex: theme.zIndex.drawer,
        }),
  };

  if (isCopa) {
    return {
      ...layoutSx,
      ...copaDrawerPaperSx,
    };
  }

  return {
    ...layoutSx,
    borderRight: isDark
      ? `1px solid ${alpha('#3b82f6', 0.14)}`
      : `1px solid ${theme.palette.divider}`,
    background: isDark
      ? 'linear-gradient(180deg, #070d18 0%, #0a101f 45%, #0d1526 100%)'
      : theme.palette.background.paper,
    boxShadow: isDark
      ? '4px 0 28px rgba(0, 0, 0, 0.45)'
      : '4px 0 20px rgba(15, 23, 42, 0.06)',
  };
}

export function getMobileDrawerBackdropSx() {
  return {
    top: MOBILE_APP_BAR_HEIGHT,
    height: `calc(100% - ${MOBILE_APP_BAR_HEIGHT}px)`,
  };
}

export function getSidebarScrollSx(theme: Theme, themeId: AppThemeId = 'default') {
  const isDark = themeId === 'dark';
  const isCopa = themeId === 'copa';

  return {
    position: 'relative' as const,
    zIndex: 1,
    flexGrow: 1,
    minHeight: 0,
    px: 0.75,
    py: 0,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    scrollbarWidth: 'thin' as const,
    scrollbarColor: isCopa
      ? `${alpha(COPA_AMARELO_BANDEIRA, 0.55)} transparent`
      : isDark
        ? `${alpha('#3b82f6', 0.45)} transparent`
        : '#87CEEB transparent',
    '&::-webkit-scrollbar': { width: '2px' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: isCopa
        ? alpha(COPA_AMARELO_BANDEIRA, 0.55)
        : isDark
          ? alpha('#3b82f6', 0.45)
          : '#87CEEB',
      borderRadius: '10px',
      '&:hover': {
        backgroundColor: isCopa
          ? COPA_AMARELO_BANDEIRA
          : isDark
            ? '#3b82f6'
            : '#5DADE2',
      },
    },
  };
}

export function getSidebarFadeGradient(theme: Theme, themeId: AppThemeId = 'default') {
  const isDark = theme.palette.mode === 'dark';
  const isCopa = themeId === 'copa';
  const solid = isCopa
    ? COPA_VERDE_BANDEIRA
    : isDark
      ? '#0a101f'
      : theme.palette.background.paper;

  return `linear-gradient(to bottom, ${solid} 50%, ${solid} 75%, transparent 100%)`;
}

/** Decoração inferior do sidebar — imagem com fade para não atrapalhar o menu */
export function getSidebarBackgroundDecorSx(
  theme: Theme,
  themeId: AppThemeId = 'default',
  expanded: boolean,
) {
  const isDark = themeId === 'dark';
  const isCopa = themeId === 'copa';
  const isRosa = themeId === 'rosa';
  const isUsa = themeId === 'usa';

  if ((isCopa || isRosa || isUsa) && !expanded) {
    return { display: 'none' };
  }

  return {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    height: expanded ? 240 : 168,
    pointerEvents: 'none' as const,
    zIndex: 0,
    overflow: 'hidden',
    maskImage:
      'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.92) 18%, rgba(0,0,0,0.45) 28%, transparent 100%)',
    WebkitMaskImage:
      'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.92) 18%, rgba(0,0,0,0.45) 28%, transparent 100%)',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      objectPosition: 'bottom center',
      display: 'block',
      opacity: isCopa || isRosa || isUsa ? 0.95 : isDark ? 0.82 : 0.30,
      transform: expanded ? 'none' : 'scale(1.15)',
      transformOrigin: 'bottom center',
      transition: theme.transitions.create(['opacity', 'transform'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
  };
}

export function getSidebarBrandSx(theme: Theme, expanded: boolean, themeId: AppThemeId = 'default') {
  if (themeId === 'copa') {
    return copaSidebarBrandSx(expanded);
  }
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: expanded ? 1.25 : 0,
    px: expanded ? 0.5 : 0.5,
    py: expanded ? 0.5 : 0.5,
    minHeight: expanded ? 72 : 64,
  };
}

export function getSidebarUserCardSx(
  theme: Theme,
  expanded: boolean,
  themeId: AppThemeId = 'default',
) {
  if (themeId === 'copa') {
    return {
      ...copaSidebarUserBoxSx,
      display: 'flex',
      justifyContent: 'center',
      ...(expanded
        ? {}
        : {
            border: 'none',
            bgcolor: 'transparent',
            mx: 0,
            p: 0.5,
          }),
    };
  }

  return {
    mx: 1,
    mt: 0.5,
    mb: 1,
    p: expanded ? 1.25 : 0.5,
    borderRadius: 2,
    display: 'flex',
    justifyContent: 'center',
    ...(expanded
      ? {
          bgcolor: themeId === 'dark'
            ? alpha('#3b82f6', 0.12)
            : alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${
            themeId === 'dark'
              ? alpha('#3b82f6', 0.35)
              : alpha(theme.palette.primary.main, 0.22)
          }`,
        }
      : {
          border: 'none',
          bgcolor: 'transparent',
          mx: 0,
        }),
  };
}

export function getSidebarRoleChipSx(theme: Theme, themeId: AppThemeId = 'default') {
  const isDark = themeId === 'dark';
  const isCopa = themeId === 'copa';

  if (isCopa) {
    return {
      height: 20,
      fontSize: '0.65rem',
      fontWeight: 600,
      letterSpacing: 0.3,
      bgcolor: alpha(COPA_AMARELO_BANDEIRA, 0.22),
      color: COPA_VERDE_BANDEIRA,
      border: `1px solid ${alpha(COPA_AMARELO_BANDEIRA, 0.55)}`,
      '& .MuiChip-label': { px: 1 },
    };
  }

  return {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 600,
    letterSpacing: 0.3,
    bgcolor: isDark
      ? alpha('#3b82f6', 0.2)
      : alpha(theme.palette.primary.main, 0.1),
    color: isDark ? '#93c5fd' : theme.palette.primary.main,
    border: `1px solid ${
      isDark
        ? alpha('#3b82f6', 0.35)
        : alpha(theme.palette.primary.main, 0.2)
    }`,
    '& .MuiChip-label': { px: 1 },
  };
}

interface MenuButtonOptions {
  selected: boolean;
  parentActive: boolean;
  level: number;
  expanded: boolean;
  isMobile: boolean;
  hasChildren: boolean;
  themeId?: AppThemeId;
}

export function getSidebarMenuButtonSx(theme: Theme, opts: MenuButtonOptions) {
  const themeId = opts.themeId ?? 'default';
  const isDark = themeId === 'dark';
  const isCopa = themeId === 'copa';
  const { selected, parentActive, level, expanded, isMobile, hasChildren } = opts;
  const isSubmenu = level > 0;

  if (isCopa) {
    return {
      ...copaMenuItemSx(selected, level),
      minHeight: isMobile ? 44 : isSubmenu ? 32 : 42,
      justifyContent: expanded ? 'initial' : 'center',
      px: expanded ? (isSubmenu ? (isMobile ? 3 : 2.5) : 1.25) : 1,
      py: isSubmenu ? 0.35 : 0.75,
      mx: isSubmenu ? 0 : 0.25,
      mb: isSubmenu ? 0 : 0.25,
      ml: isSubmenu && expanded ? (isMobile ? 3.5 : 2.5) : 0,
    };
  }

  const accent = isDark ? '#3b82f6' : theme.palette.primary.main;

  const selectedMenuStyles = {
    bgcolor: isDark
      ? alpha('#3b82f6', 0.22)
      : alpha(theme.palette.primary.main, 0.12),
    color: isDark ? '#e2e8f0' : theme.palette.primary.main,
    fontWeight: 600,
    boxShadow: `inset 3px 0 0 ${accent}`,
    '& .MuiListItemIcon-root': {
      color: isDark ? '#93c5fd' : theme.palette.primary.main,
    },
    '& .MuiTypography-root': { fontWeight: 600 },
    '&:hover': {
      bgcolor: isDark
        ? alpha('#3b82f6', 0.28)
        : alpha(theme.palette.primary.main, 0.16),
    },
  };

  return {
    borderRadius: isSubmenu ? 1 : 2,
    minHeight: isMobile ? 44 : isSubmenu ? 32 : 42,
    justifyContent: expanded ? 'initial' : 'center',
    px: expanded ? (isSubmenu ? (isMobile ? 3 : 2.5) : 1.25) : 1,
    py: isSubmenu ? 0.35 : 0.75,
    mx: isSubmenu ? 0 : 0.25,
    mb: isSubmenu ? 0 : 0.25,
    ml: isSubmenu && expanded ? (isMobile ? 3.5 : 2.5) : 0,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),
    ...(isSubmenu &&
      !selected && {
      bgcolor: 'transparent',
      '&:hover': {
        bgcolor: isDark
          ? alpha('#3b82f6', 0.08)
          : alpha(theme.palette.primary.main, 0.05),
      },
    }),
    ...(selected && selectedMenuStyles),
    ...(!selected &&
      !isSubmenu &&
      !parentActive && {
        color: isDark ? alpha('#e2e8f0', 0.85) : theme.palette.text.primary,
        '&:hover': {
          bgcolor: isDark
            ? alpha('#3b82f6', 0.1)
            : alpha(theme.palette.primary.main, 0.06),
        },
        '& .MuiListItemIcon-root': {
          color: isDark ? alpha('#94a3b8', 0.95) : theme.palette.text.secondary,
        },
      }),
    ...(hasChildren &&
      !selected &&
      !parentActive && {
        '& .MuiSvgIcon-root:last-of-type': {
          color: isDark ? alpha('#94a3b8', 0.8) : theme.palette.text.secondary,
        },
      }),
  };
}

export function getSidebarIconWrapSx(theme: Theme, selected: boolean) {
  const isDark = theme.palette.mode === 'dark';

  return {
    minWidth: 0,
    mr: 'auto',
    justifyContent: 'center',
    '& svg': {
      fontSize: '1.35rem',
      transition: theme.transitions.create('transform', { duration: 200 }),
    },
    ...(selected && {
      '& svg': {
        color: isDark ? '#93c5fd' : theme.palette.primary.main,
      },
    }),
  };
}

export function getSidebarStickyBg(themeId: AppThemeId, theme: Theme, isDarkMode: boolean): string {
  if (themeId === 'copa') return COPA_VERDE_BANDEIRA;
  if (isDarkMode) return '#0a101f';
  return theme.palette.background.paper;
}

export function getSidebarSubmenuContainerSx() {
  return {
    py: 0.25,
    pb: 0.5,
  };
}
